import {
    CartPersistenceError,
    type CartAttribute,
    type CartItem,
    type CartPersistedState,
    type CartStorageEnvelope,
} from "../types/cart.types";
import { reportCartDiagnostic } from "../config/cartRuntimeConfig";

export const CART_STORAGE_KEY = "shopping_cart" as const;
export const CART_STORAGE_VERSION = 1 as const;

const EMPTY_UPDATED_AT = "1970-01-01T00:00:00.000Z";
const MAX_RAW_DIAGNOSTIC_LENGTH = 30_000;
const MAX_ITEM_DIAGNOSTICS = 100;

export const createEmptyCartSnapshot = (): CartPersistedState => ({
    items: [],
    revision: 0,
    updatedAt: EMPTY_UPDATED_AT,
});

const isFiniteNumber = (value: unknown): value is number =>
    typeof value === "number" && Number.isFinite(value);

const isCartItem = (value: unknown): value is CartItem => {
    if (typeof value !== "object" || value === null || Array.isArray(value)) return false;

    const item = value as Partial<CartItem>;
    return (
        typeof item.idProduct === "string" &&
        typeof item.idProductVariant === "string" &&
        typeof item.name === "string" &&
        isFiniteNumber(item.price) &&
        isFiniteNumber(item.quantity) &&
        item.quantity > 0 &&
        typeof item.image === "string" &&
        (item.stock === undefined || isFiniteNumber(item.stock)) &&
        Array.isArray(item.attributes)
    );
};

const isCartItemArray = (value: unknown): value is CartItem[] =>
    Array.isArray(value) && value.every(isCartItem);

const asRecord = (value: unknown): Record<string, unknown> | null =>
    typeof value === "object" && value !== null && !Array.isArray(value)
        ? (value as Record<string, unknown>)
        : null;

const getValueType = (value: unknown): string => {
    if (value === null) return "null";
    if (Array.isArray(value)) return "array";
    return typeof value;
};

const serializeErrorForDiagnostic = (error: unknown): Record<string, unknown> => {
    if (!(error instanceof Error)) {
        return { type: getValueType(error), value: String(error) };
    }

    return {
        name: error.name,
        message: error.message,
        stack: error.stack ?? null,
    };
};

const describeItemCandidate = (value: unknown, index: number): Record<string, unknown> => {
    const item = asRecord(value);
    if (!item) {
        return { index, type: getValueType(value), recoverable: false };
    }

    const selectedValues = [
        "idProduct",
        "idProducto",
        "productId",
        "idProductVariant",
        "idProductoVariante",
        "productVariantId",
        "variantId",
        "quantity",
        "cantidad",
        "price",
        "precio",
        "stock",
        "existencia",
    ].reduce<Record<string, unknown>>((result, key) => {
        if (item[key] !== undefined) result[key] = item[key];
        return result;
    }, {});

    return {
        index,
        type: "object",
        keys: Object.keys(item),
        fieldTypes: Object.fromEntries(
            Object.entries(item).map(([key, fieldValue]) => [key, getValueType(fieldValue)]),
        ),
        selectedValues,
        recoverable: recoverCartItem(item) !== null,
    };
};

const describeCartStorageValue = (rawValue: string | null): Record<string, unknown> => {
    const rawTruncated = rawValue !== null && rawValue.length > MAX_RAW_DIAGNOSTIC_LENGTH;
    const rawForDiagnostic =
        rawValue === null || !rawTruncated
            ? rawValue
            : `${rawValue.slice(0, MAX_RAW_DIAGNOSTIC_LENGTH / 2)}<TRUNCATED>${rawValue.slice(
                  -MAX_RAW_DIAGNOSTIC_LENGTH / 2,
              )}`;

    let parsed: unknown;
    let parseError: Record<string, unknown> | null = null;
    try {
        parsed = rawValue === null ? null : JSON.parse(rawValue);
    } catch (error) {
        parseError = serializeErrorForDiagnostic(error);
    }

    const root = asRecord(parsed);
    const state = root ? asRecord(root.state) : null;
    const items = Array.isArray(parsed)
        ? parsed
        : Array.isArray(state?.items)
          ? state.items
          : Array.isArray(root?.items)
            ? root.items
            : null;

    return {
        storageKey: CART_STORAGE_KEY,
        origin: typeof window !== "undefined" ? window.location?.origin : undefined,
        rawPresent: rawValue !== null,
        rawLength: rawValue?.length ?? 0,
        rawTruncated,
        rawValue: rawForDiagnostic,
        jsonParseSucceeded: parseError === null,
        parseError,
        rootType: getValueType(parsed),
        rootKeys: root ? Object.keys(root) : [],
        stateType: getValueType(root?.state),
        stateKeys: state ? Object.keys(state) : [],
        envelopeVersion: root?.version,
        revision: state?.revision ?? root?.revision,
        updatedAt: state?.updatedAt ?? root?.updatedAt,
        itemsContainerFound: items !== null,
        itemCount: items?.length ?? null,
        itemDiagnosticsTruncated: (items?.length ?? 0) > MAX_ITEM_DIAGNOSTICS,
        itemDiagnostics: items
            ?.slice(0, MAX_ITEM_DIAGNOSTICS)
            .map((item, index) => describeItemCandidate(item, index)),
        capabilities: {
            localStorageAvailable: typeof window !== "undefined" && !!window.localStorage,
            indexedDbAvailable: typeof indexedDB !== "undefined",
            webLocksAvailable:
                typeof navigator !== "undefined" && typeof navigator.locks?.request === "function",
        },
    };
};

const firstDefined = (record: Record<string, unknown>, keys: string[]): unknown => {
    for (const key of keys) {
        if (record[key] !== undefined && record[key] !== null) return record[key];
    }

    return undefined;
};

const toNonEmptyString = (value: unknown): string | null => {
    if (typeof value === "string" && value.trim()) return value.trim();
    if (isFiniteNumber(value)) return String(value);
    return null;
};

const toFiniteNumber = (value: unknown): number | null => {
    if (isFiniteNumber(value)) return value;
    if (typeof value !== "string" || !value.trim()) return null;

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const recoverAttributes = (value: unknown): CartAttribute[] => {
    if (!Array.isArray(value)) return [];

    return value.flatMap((candidate) => {
        const attribute = asRecord(candidate);
        if (!attribute) return [];

        const name = toNonEmptyString(
            firstDefined(attribute, ["name", "nombre", "nombreAtributo"]),
        );
        const attributeValue = toNonEmptyString(
            firstDefined(attribute, ["value", "valor", "nombreAtributoValor"]),
        );
        if (!name || !attributeValue) return [];

        return [{ name, value: attributeValue }];
    });
};

const recoverCartItem = (value: unknown): CartItem | null => {
    const candidate = asRecord(value);
    if (!candidate) return null;

    const idProduct = toNonEmptyString(
        firstDefined(candidate, ["idProduct", "idProducto", "productId"]),
    );
    const idProductVariant = toNonEmptyString(
        firstDefined(candidate, [
            "idProductVariant",
            "idProductoVariante",
            "productVariantId",
            "variantId",
        ]),
    );
    if (!idProduct || !idProductVariant) return null;

    const rawQuantity = toFiniteNumber(firstDefined(candidate, ["quantity", "cantidad"]));
    const rawPrice = toFiniteNumber(firstDefined(candidate, ["price", "precio"]));
    const rawStock = toFiniteNumber(firstDefined(candidate, ["stock", "existencia"]));
    const name = toNonEmptyString(
        firstDefined(candidate, ["name", "nombre", "productName", "sku"]),
    );
    const image = firstDefined(candidate, ["image", "urlImagenPrincipal", "productImage"]);

    return {
        idProduct,
        idProductVariant,
        name: name ?? "Producto recuperado",
        price: rawPrice !== null && rawPrice >= 0 ? rawPrice : 0,
        quantity: rawQuantity !== null && rawQuantity > 0 ? rawQuantity : 1,
        image: typeof image === "string" ? image : "",
        ...(rawStock !== null && rawStock >= 0 ? { stock: rawStock } : {}),
        attributes: recoverAttributes(firstDefined(candidate, ["attributes", "atributos"])),
    };
};

const mergeRecoveredItems = (items: CartItem[]): CartItem[] => {
    const recoveredByLine = new Map<string, CartItem>();

    for (const item of items) {
        const key = `${item.idProduct}\u0000${item.idProductVariant}`;
        const existing = recoveredByLine.get(key);
        if (!existing) {
            recoveredByLine.set(key, item);
            continue;
        }

        const stock = item.stock ?? existing.stock;
        const combinedQuantity = existing.quantity + item.quantity;
        recoveredByLine.set(key, {
            ...existing,
            name: existing.name === "Producto recuperado" ? item.name : existing.name,
            price: existing.price === 0 ? item.price : existing.price,
            image: existing.image || item.image,
            ...(stock !== undefined ? { stock } : {}),
            quantity: stock === undefined ? combinedQuantity : Math.min(combinedQuantity, stock),
            attributes: existing.attributes.length > 0 ? existing.attributes : item.attributes,
        });
    }

    return Array.from(recoveredByLine.values());
};

const recoverCartSnapshot = (rawValue: string | null): CartPersistedState | null => {
    if (rawValue === null) return createEmptyCartSnapshot();

    let parsed: unknown;
    try {
        parsed = JSON.parse(rawValue);
    } catch {
        return null;
    }

    const root = asRecord(parsed);
    const state = root ? asRecord(root.state) : null;
    const itemCandidates = Array.isArray(parsed)
        ? parsed
        : Array.isArray(state?.items)
          ? state.items
          : Array.isArray(root?.items)
            ? root.items
            : null;
    if (!itemCandidates) return null;

    const items = mergeRecoveredItems(
        itemCandidates.flatMap((candidate) => {
            const recovered = recoverCartItem(candidate);
            return recovered ? [recovered] : [];
        }),
    );
    const previousRevision = toFiniteNumber(state?.revision ?? root?.revision);

    return {
        items,
        revision: previousRevision !== null && previousRevision >= 0 ? previousRevision + 1 : 1,
        updatedAt: new Date().toISOString(),
    };
};

export const parseCartSnapshot = (rawValue: string | null): CartPersistedState => {
    if (rawValue === null) return createEmptyCartSnapshot();

    try {
        const parsed = JSON.parse(rawValue) as {
            state?: { items?: unknown; revision?: unknown; updatedAt?: unknown };
            version?: unknown;
        };
        const items = parsed?.state?.items;

        if (!isCartItemArray(items)) {
            throw new CartPersistenceError(
                "STORAGE_CORRUPTED",
                "El estado persistido del carrito no contiene una lista de items válida.",
            );
        }

        const revision =
            typeof parsed.state?.revision === "number" &&
            Number.isFinite(parsed.state.revision) &&
            parsed.state.revision >= 0
                ? parsed.state.revision
                : 0;
        const updatedAt =
            typeof parsed.state?.updatedAt === "string" ? parsed.state.updatedAt : EMPTY_UPDATED_AT;

        return { items, revision, updatedAt };
    } catch (error) {
        if (error instanceof CartPersistenceError) throw error;

        throw new CartPersistenceError(
            "STORAGE_CORRUPTED",
            "No se pudo interpretar el estado persistido del carrito.",
            error,
        );
    }
};

const getLocalStorage = (): Storage => {
    if (typeof window === "undefined" || !window.localStorage) {
        throw new CartPersistenceError(
            "STORAGE_READ_FAILED",
            "localStorage no está disponible para leer el carrito.",
        );
    }

    return window.localStorage;
};

export const collectCartStorageDiagnostic = (): Record<string, unknown> => {
    try {
        return describeCartStorageValue(getLocalStorage().getItem(CART_STORAGE_KEY));
    } catch (error) {
        return {
            storageKey: CART_STORAGE_KEY,
            diagnosticReadFailed: true,
            diagnosticReadError: serializeErrorForDiagnostic(error),
            capabilities: {
                indexedDbAvailable: typeof indexedDB !== "undefined",
                webLocksAvailable:
                    typeof navigator !== "undefined" &&
                    typeof navigator.locks?.request === "function",
            },
        };
    }
};

type ReadCartSnapshotOptions = {
    persistRecovery?: boolean;
};

export const readCartSnapshot = (options: ReadCartSnapshotOptions = {}): CartPersistedState => {
    let rawValue: string | null;
    try {
        rawValue = getLocalStorage().getItem(CART_STORAGE_KEY);
        return parseCartSnapshot(rawValue);
    } catch (error) {
        if (error instanceof CartPersistenceError) {
            if (error.code !== "STORAGE_CORRUPTED") throw error;

            const recoveredSnapshot = recoverCartSnapshot(rawValue!);
            if (recoveredSnapshot) {
                if (options.persistRecovery) {
                    reportCartDiagnostic({
                        kind: "STORAGE_RECOVERED",
                        timestampUtc: new Date().toISOString(),
                        error,
                        context: {
                            storageBeforeRecovery: describeCartStorageValue(rawValue!),
                            recoveryResult: {
                                recoveredItemCount: recoveredSnapshot.items.length,
                                recoveredRevision: recoveredSnapshot.revision,
                                recoveredItems: recoveredSnapshot.items.map((item) => ({
                                    idProduct: item.idProduct,
                                    idProductVariant: item.idProductVariant,
                                    quantity: item.quantity,
                                    price: item.price,
                                    stock: item.stock,
                                    attributeCount: item.attributes.length,
                                })),
                            },
                        },
                    });
                    writeCartSnapshot(recoveredSnapshot);
                    console.warn(
                        `Se reconstruyó el carrito persistido y se recuperaron ${recoveredSnapshot.items.length} items.`,
                        error,
                    );
                }
                return recoveredSnapshot;
            }

            if (options.persistRecovery) {
                reportCartDiagnostic({
                    kind: "STORAGE_DISCARDED",
                    timestampUtc: new Date().toISOString(),
                    error,
                    context: {
                        storageBeforeDiscard: describeCartStorageValue(rawValue!),
                        reason: "El JSON no pudo interpretarse ni reconstruirse.",
                    },
                });
                try {
                    getLocalStorage().removeItem(CART_STORAGE_KEY);
                } catch (recoveryError) {
                    throw new CartPersistenceError(
                        "STORAGE_WRITE_FAILED",
                        "No se pudo eliminar el carrito persistido corrupto.",
                        recoveryError,
                    );
                }

                console.warn(
                    "Se eliminó el carrito persistido porque estaba corrupto y no podía recuperarse.",
                    error,
                );
            }

            return createEmptyCartSnapshot();
        }

        throw new CartPersistenceError(
            "STORAGE_READ_FAILED",
            "No se pudo leer el carrito persistido.",
            error,
        );
    }
};

export const writeCartSnapshot = (snapshot: CartPersistedState): void => {
    const envelope: CartStorageEnvelope = {
        state: snapshot,
        version: CART_STORAGE_VERSION,
    };

    try {
        getLocalStorage().setItem(CART_STORAGE_KEY, JSON.stringify(envelope));
    } catch (error) {
        if (error instanceof CartPersistenceError) {
            throw new CartPersistenceError(
                "STORAGE_WRITE_FAILED",
                "localStorage no está disponible para guardar el carrito.",
                error,
            );
        }

        throw new CartPersistenceError(
            "STORAGE_WRITE_FAILED",
            "No se pudo guardar el carrito persistido.",
            error,
        );
    }
};
